import { makeAutoObservable, runInAction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  ProductTemplate,
  ProductTemplateExtendedStatus,
  ProductTemplateSortBy,
  ProductTemplateSortOrder,
} from 'types/productServiceV2';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import {
  changeArchiveStatus,
  duplicateProductTemplate,
  generateTemplateJson,
  getProductTemplates,
} from 'api/operations/productServiceV2';
import { createContext, useContext } from 'react';
import { getProductLinesByEffectiveDateAndState } from 'api/operations/domainServiceV2';
import { PaginationDelegate, FiltersObject } from 'store/general/paginationDelegate';
import { getCurrentDateInMDTFormat } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';
import { CreateTemplateModalStore } from './createTemplateModalStore';

interface DuplicateTemplateFields extends DefaultFormFields {
  internalName: FormControl<string>;
  externalName: FormControl<string>;
}

export type WarningModalType = 'ARCHIVE' | 'UNARCHIVE';

export interface ProductTemplatesFilter extends FiltersObject {
  status: ProductTemplateExtendedStatus;
  productLineId: ProductLineId | '';
  state: string;
  effectiveYear: string;
  searchProductName: string;
}

export const duplicateTemplateFormController = new FormController({
  internalName: new FormControl<string>('', { optional: false, trim: true }),
  externalName: new FormControl<string>('', { optional: false, trim: true }),
});

// TODO extract modals to UI stores
export default class RFQProductTemplatesStore {
  private _rootStore: RootStore;

  private _isLoading = false;
  private _productTemplates: ProductTemplate[] = [];
  private _availableProductLineIds: ProductLineId[] = [];
  private _availableStates: string[] = [];

  // Pagination delegate
  public paginationDelegate: PaginationDelegate<
    ProductTemplateSortBy,
    ProductTemplateSortOrder,
    ProductTemplatesFilter
  >;

  // Modals
  private _currentProductTemplate: ProductTemplate | null = null;
  private _modalLoading = false;

  // Create Template Modal
  public createTemplateModalDelegate = new CreateTemplateModalStore();

  // Warning Modal
  private _isWarningModalOpen = false;
  private _warningModalType: WarningModalType | null = null;

  // Duplicate Template Modal
  private _isDuplicateModalOpen = false;
  private _duplicateTemplateForm: FormController<DuplicateTemplateFields>;

  // Template Info Modal
  private _isTemplateInfoModalOpen = false;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get modalLoading(): boolean {
    return this._modalLoading;
  }

  public get productTemplates(): ProductTemplate[] {
    return this._productTemplates;
  }

  public get currentProductTemplate(): ProductTemplate | null {
    return this._currentProductTemplate;
  }

  public get isWarningModalOpen(): boolean {
    return this._isWarningModalOpen;
  }

  public get warningModalType(): WarningModalType | null {
    return this._warningModalType;
  }

  public get isDuplicateModalOpen(): boolean {
    return this._isDuplicateModalOpen;
  }

  public get duplicateTemplateForm(): FormController<DuplicateTemplateFields> {
    return this._duplicateTemplateForm;
  }

  public get isTemplateInfoModalOpen(): boolean {
    return this._isTemplateInfoModalOpen;
  }

  public get showInitialSeedFeature(): boolean {
    return this._rootStore.domainStoreV2.showInitialSeedFeature;
  }

  public get availableProductLineIds(): ProductLineId[] {
    return this._availableProductLineIds;
  }

  public get availableStates(): string[] {
    return this._availableStates;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private setCurrentProductTemplate(template: ProductTemplate | null): void {
    this._currentProductTemplate = template;
  }

  private setIsWarningModalOpen(status: boolean): void {
    this._isWarningModalOpen = status;
  }

  private setWarningModalType(type: WarningModalType | null): void {
    this._warningModalType = type;
  }

  private setIsDuplicateModalOpen(status: boolean): void {
    this._isDuplicateModalOpen = status;
  }

  private setIsTemplateInfoModalOpen(status: boolean): void {
    this._isTemplateInfoModalOpen = status;
  }

  private setModalLoading(status: boolean): void {
    this._modalLoading = status;
  }

  // Other
  public openWarningModal(productTemplate: ProductTemplate, modalType: WarningModalType): void {
    this.setWarningModalType(modalType);
    this.setIsWarningModalOpen(true);
    this.setCurrentProductTemplate(productTemplate);
  }

  public closeWarningModal(): void {
    this.setWarningModalType(null);
    this.setIsWarningModalOpen(false);
    this.setCurrentProductTemplate(null);
  }

  public openTemplateInfoModal(productTemplate: ProductTemplate): void {
    this.setIsTemplateInfoModalOpen(true);
    this.setCurrentProductTemplate(productTemplate);
  }

  public closeTemplateInfoModal(): void {
    this.setIsTemplateInfoModalOpen(false);
    this.setCurrentProductTemplate(null);
  }

  public openDuplicateTemplateModal(productTemplate: ProductTemplate): void {
    this.setCurrentProductTemplate(productTemplate);
    this.setIsDuplicateModalOpen(true);

    this.duplicateTemplateForm.populateFields({
      externalName: `${productTemplate.name} ${getCurrentDateInMDTFormat()}`,
      internalName: `${productTemplate.internalName} ${getCurrentDateInMDTFormat()}`,
    });
  }

  public closeDuplicateTemplateModal(): void {
    this.setCurrentProductTemplate(null);
    this.setIsDuplicateModalOpen(false);
    this.duplicateTemplateForm.resetForm();
  }

  public async openCreateTemplateModal(): Promise<void> {
    this.createTemplateModalDelegate.open();
    await this.createTemplateModalDelegate.getInitialData();
  }

  public closeCreateTemplateModal(): void {
    this.createTemplateModalDelegate.close();
    this.createTemplateModalDelegate.resetForm();
  }

  // API methods
  public async getInitialData(): Promise<void> {
    this.setIsLoading(true);

    this._availableStates = this._rootStore.domainStoreV2.domainHeader.properties.eligibleStates || [];
    if (this._availableStates.length > 0) {
      this.paginationDelegate.setFilterValue('state', this._availableStates[0]);
    }

    await this.getProductLines();
    await this.getProductTemplates();

    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  public async getProductLines(): Promise<void> {
    const domainProductLines = await getProductLinesByEffectiveDateAndState(
      this._rootStore.domainStoreV2.domainHeader.id,
      `${this.paginationDelegate.filters.effectiveYear}-01-01`,
      this.paginationDelegate.filters.state,
    );

    runInAction(() => {
      // Show only custom product lines in the product line filter
      this._availableProductLineIds = domainProductLines
        .filter((pLine) => pLine.rateSegment === 'CUSTOM')
        .map((pLine) => pLine.productLineId);
      // Set the first available product line ID if current one is not in the list
      if (!this._availableProductLineIds.includes(this.paginationDelegate.filters.productLineId as ProductLineId)) {
        this.paginationDelegate.setFilterValue('productLineId', this._availableProductLineIds[0] || '');
      }
    });
  }

  public async getProductTemplates(): Promise<void> {
    const { productLineId, state, effectiveYear } = this.paginationDelegate.filters;
    if (!productLineId || !state || !effectiveYear) {
      return;
    }

    this.setIsLoading(true);
    const { page, sortBy, sortOrder, filter } = this.paginationDelegate.paginationInput;
    const result = await getProductTemplates({
      page,
      sortBy,
      sortOrder,
      filter: {
        status: filter.status === 'IM_GENERATED' ? null : filter.status,
        productName: filter.searchProductName || null,
        productLine: filter.productLineId ? filter.productLineId : null,
        state: filter.state || null,
        effectiveYear: filter.effectiveYear || null,
      },
    });
    runInAction(() => {
      this.paginationDelegate.setTotalElements(result.totalElements);
      this.paginationDelegate.setTotalPages(result.totalPages);
      this._productTemplates = result.result;

      this.setIsLoading(false);
    });
  }

  public async duplicateProductTemplate(): Promise<void> {
    if (this.currentProductTemplate?.id) {
      this.setModalLoading(true);
      await duplicateProductTemplate({
        id: this.currentProductTemplate?.id,
        externalName: this.duplicateTemplateForm.fields.externalName.value,
        internalName: this.duplicateTemplateForm.fields.internalName.value,
      });
      runInAction(() => {
        this.setModalLoading(false);
      });
    }
  }

  public async changeArchiveStatus(archiveStatus: boolean): Promise<void> {
    if (this.currentProductTemplate?.id) {
      this.setIsLoading(true);
      await changeArchiveStatus(this.currentProductTemplate?.id, archiveStatus);
      runInAction(() => {
        void this.getProductTemplates();
        this.setIsLoading(false);
      });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public async getTemplateJsonLinkForDownload(templateId: string): Promise<string> {
    try {
      const link = await generateTemplateJson(templateId);
      if (link) {
        return link;
      }
    } catch (error) {
      toastManager.addToast('Something went wrong during file download', ToastColorTypes.WARNING);
    }

    return '';
  }

  // eslint-disable-next-line class-methods-use-this
  public downloadGeneratedPdfLink(id: string, name: string): string {
    return `https://${COMMON_URLS.api}/proposalV3/Prod/download?sponsorId=${this._rootStore.userStore.sponsorId}&templateId=${id}&templateName=${name}`;
  }

  constructor() {
    this._rootStore = getRootStoreInstance();

    this._duplicateTemplateForm = duplicateTemplateFormController;

    this.paginationDelegate = new PaginationDelegate<
      ProductTemplateSortBy,
      ProductTemplateSortOrder,
      ProductTemplatesFilter
    >({
      defaultSortBy: ProductTemplateSortBy.NAME,
      defaultSortOrder: ProductTemplateSortOrder.ASC,
      filters: {
        status: 'IM_GENERATED',
        productLineId: 'CustomDental',
        state: '',
        effectiveYear: new Date().getFullYear().toString(),
        searchProductName: '',
      },
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new RFQProductTemplatesStore());

export const useRFQProductTemplatesStore = (): RFQProductTemplatesStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
